import { DOCUMENT, inject, Injectable } from '@angular/core';
import { createFontStack } from '@capsizecss/core';

import { DeployUrlService } from 'src/services/deploy-url/deploy-url.service';
import { LocaleService } from 'src/services/locale/locale.service';

import { fontsConfig } from './entities/const';
import type {
    IFont,
    IFontsConfig,
    TFontFamily,
    TFontFormat,
    TFontMetric,
    TFontOpticalMetrics,
    TFontStyle,
    TFontWeightName,
    TFontWeightNumeric,
} from './entities/types';

@Injectable({ providedIn: 'root' })
export class FontService {
    private readonly document = inject<Document>(DOCUMENT);
    private localeService = inject(LocaleService);
    private deployUrlService = inject(DeployUrlService);

    public readonly FONT_STYLE_TAG_ID = 'website-fonts';
    private readonly PATH_TO_FONTS_DIRECTORY = 'assets/fonts';

    public init(config: IFontsConfig = fontsConfig): void {
        const isFontStylesNotInserted = this.document.getElementById(this.FONT_STYLE_TAG_ID) === null;
        if (isFontStylesNotInserted) {
            const currentLocaleSlug = this.localeService.getCurrentLocaleSlug();
            const localFont = config.localFonts.find((font) => font.locales.includes(currentLocaleSlug));
            const font = localFont || config.defaultFont;
            const styleContent = this.generateStyleContent(font, config.fontFamilyFallback);
            this.insertFontStyles(styleContent);
        }
    }

    private generateStyleContent(font: IFont, fontFamilyFallback: string): Text {
        let content = '';

        content += this.generateMainFontFaces(font);
        content += this.generateAdjustedFallbacks(font.opticalMetrics, font.averageWidths, font.xHeights);
        content += this.generateBodyFontStyles(font, fontFamilyFallback);

        return this.document.createTextNode(content);
    }

    private generateMainFontFaces(font: IFont): string {
        const fontPath = this.deployUrlService.deployUrl + this.PATH_TO_FONTS_DIRECTORY;

        return Object.keys(font.weights).reduce((acc: string, fontWeight: TFontWeightNumeric) => {
            font.styles.forEach((fontStyle) => {
                if (font.subsets && font.subsets?.length) {
                    font.subsets.forEach(({ name: subsetName, unicodeRange }) => {
                        acc += this.generateFontFace({
                            family: font.family,
                            path: fontPath,
                            version: font.version,
                            weight: fontWeight,
                            weightName: font.weights[fontWeight],
                            style: fontStyle,
                            formats: font.formats,
                            subsetName,
                            unicodeRange,
                        });
                    });
                } else {
                    acc += this.generateFontFace({
                        family: font.family,
                        path: fontPath,
                        version: font.version,
                        weight: fontWeight,
                        weightName: font.weights[fontWeight],
                        style: fontStyle,
                        formats: font.formats,
                    });
                }
            });

            return acc;
        }, '');
    }

    private generateAdjustedFallbacks(
        opticalMetrics: TFontOpticalMetrics,
        averageWidths: TFontMetric,
        xHeights: TFontMetric = {}
    ): string {
        if (!opticalMetrics) {
            return '';
        }

        if (!averageWidths) {
            const fallbackMetrics = opticalMetrics.fallbacksDefault || [];
            const { fontFaces } = createFontStack([opticalMetrics.main, ...fallbackMetrics]);
            return fontFaces;
        }

        let fontFacesRendered = '';

        for (const [weight, xWidthAvg] of Object.entries(averageWidths)) {
            const fallbackMetrics = opticalMetrics.fallbacks[weight] || opticalMetrics.fallbacksDefault || [];

            const { fontFaces } = createFontStack(
                [
                    {
                        ...opticalMetrics.main,
                        xWidthAvg,
                        xHeight: xHeights[weight] || opticalMetrics.main.xHeight,
                    },
                    ...fallbackMetrics,
                ],
                {
                    fontFaceProperties: {
                        fontDisplay: 'swap',
                        fontWeight: weight,
                    },
                }
            );
            fontFacesRendered += fontFaces;
        }
        return fontFacesRendered;
    }

    private generateFontFace(fontFaceData: {
        family: TFontFamily;
        path: string;
        version: string;
        weight: TFontWeightNumeric;
        weightName: TFontWeightName;
        style: TFontStyle;
        formats: TFontFormat[];
        subsetName?: string;
        unicodeRange?: string;
    }): string {
        const { family, path, version, weight, weightName, style, formats, subsetName, unicodeRange } = fontFaceData;
        const subsetPath = subsetName ? subsetName + '/' : '';
        const fontUrls: string = formats
            .map((format) => {
                return `url(${path}/${family}/${version}/${subsetPath}${family}-${weightName}.${format}) format("${format}")`;
            })
            .join(',');

        const unicodeRangeProperty = unicodeRange ? `unicode-range:${unicodeRange};` : '';

        return (
            '@font-face{' +
            `font-family:${family};` +
            `src:${fontUrls};` +
            `font-weight:${weight};` +
            `font-style:"${style}";` +
            `${unicodeRangeProperty}` +
            `font-display:swap;` +
            '}'
        );
    }

    private generateBodyFontStyles(font: IFont, fontFamilyFallback: string): string {
        const { fontFamily } = createFontStack([font.opticalMetrics.main, ...font.opticalMetrics.fallbacksDefault]);
        return `body{font-family: ${fontFamily}, ${fontFamilyFallback};}`;
    }

    private insertFontStyles(styleContent: Text): void {
        const styleElement = this.document.createElement('style');

        styleElement.id = this.FONT_STYLE_TAG_ID;
        styleElement.appendChild(styleContent);
        this.document.head.appendChild(styleElement);
    }
}
